import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiUser, FiMail, FiPhone, FiPackage, FiSettings, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { usersAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import './EditUser.css';

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { partner } = useContext(AuthContext);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    company: '',
    isActive: true
  });
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await usersAPI.getById(id);
      
      if (response.success) {
        const userData = response.user;
        setUser(userData);
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          phoneNumber: userData.phoneNumber || '',
          company: userData.company || '',
          isActive: userData.isActive !== undefined ? userData.isActive : true
        });
      } else {
        setError(response.message || 'Failed to load user details');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      setError('Failed to load user details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    try {
      setSaving(true);
      
      const response = await usersAPI.update(id, formData);
      
      if (response.success) {
        toast.success('User updated successfully');
        navigate(`/users/${id}`);
      } else {
        toast.error(response.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/users/${id}`);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    
    try {
      setResettingPassword(true);
      const response = await usersAPI.update(id, { password: passwordData.newPassword });
      
      if (response.success) {
        toast.success('Password reset successfully');
        setShowPasswordReset(false);
        setPasswordData({ newPassword: '', confirmPassword: '' });
      } else {
        toast.error(response.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setResettingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  if (loading) {
    return (
      <div className="edit-user-page">
        <div className="headerSection">
          <div className="header-content">
            <div className="header-left">
              <h2>Edit User</h2>
              <p>Loading user information...</p>
            </div>
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              <FiArrowLeft />
              Back to Dashboard
            </button>
          </div>
        </div>
        <div className="dataArea-content">
          <div className="loading">Loading user details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="edit-user-page">
        <div className="headerSection">
          <div className="header-content">
            <div className="header-left">
              <h2>Edit User</h2>
              <p>Error loading user information</p>
            </div>
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              <FiArrowLeft />
              Back to Dashboard
            </button>
          </div>
        </div>
        <div className="dataArea-content">
          <div className="error-message">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={fetchUserDetails}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="edit-user-page">
        <div className="headerSection">
          <div className="header-content">
            <div className="header-left">
              <h2>Edit User</h2>
              <p>User not found</p>
            </div>
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              <FiArrowLeft />
              Back to Dashboard
            </button>
          </div>
        </div>
        <div className="dataArea-content">
          <div className="error-message">
            <p>User not found</p>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-user-page">
      <div className="headerSection">
        <div className="header-content">
          <div className="header-left">
            <h2>Edit User</h2>
            <p>Update user information</p>
          </div>
          <button className="back-btn" onClick={() => navigate(`/users/${id}`)}>
            <FiArrowLeft />
            Back to User Details
          </button>
        </div>
      </div>

      <div className="dataArea-content">
        <div className="edit-user-container">
          <form onSubmit={handleSubmit} className="edit-user-form">
            <div className="form-section">
              <h3>Basic Information</h3>
              
              <div className="form-group">
                <label htmlFor="name">
                  <FiUser />
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Enter user name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  <FiMail />
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Enter user email"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phoneNumber">
                  <FiPhone />
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Enter phone number"
                />
              </div>

              <div className="form-group">
                <label htmlFor="company">
                  <FiSettings />
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Enter company name"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Account Status</h3>
              
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="form-checkbox"
                  />
                  <span className="checkmark"></span>
                  Active Account
                </label>
                <p className="help-text">
                  When unchecked, the user will not be able to access any extensions.
                </p>
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h3>Password Management</h3>
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  onClick={() => setShowPasswordReset(!showPasswordReset)}
                >
                  <FiLock />
                  {showPasswordReset ? 'Cancel Password Reset' : 'Reset Password'}
                </button>
              </div>

              {showPasswordReset && (
                <div className="password-reset-section">
                  <div className="form-group">
                    <label htmlFor="newPassword">New Password</label>
                    <div className="password-input-wrapper">
                      <FiLock className="input-icon" />
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        id="newPassword"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                        placeholder="Enter new password (min 6 characters)"
                        required={showPasswordReset}
                        minLength={6}
                        className="form-control"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => togglePasswordVisibility('new')}
                      >
                        {showPasswords.new ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <div className="password-input-wrapper">
                      <FiLock className="input-icon" />
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        id="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        placeholder="Confirm new password"
                        required={showPasswordReset}
                        className="form-control"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => togglePasswordVisibility('confirm')}
                      >
                        {showPasswords.confirm ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                    {passwordData.confirmPassword && (
                      <div className={`password-match ${passwordData.newPassword === passwordData.confirmPassword ? 'valid' : 'invalid'}`}>
                        {passwordData.newPassword === passwordData.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                      </div>
                    )}
                  </div>

                  {showPasswordReset && (
                    <div className="form-group">
                      <button
                        type="button"
                        className="btn btn-warning"
                        onClick={handlePasswordReset}
                        disabled={resettingPassword || !passwordData.newPassword || passwordData.newPassword !== passwordData.confirmPassword}
                      >
                        {resettingPassword ? 'Resetting...' : 'Reset Password Now'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="form-section">
              <h3>Current Information</h3>
              
              <div className="info-item">
                <label>User ID:</label>
                <span>{user._id}</span>
              </div>
              
              <div className="info-item">
                <label>Role:</label>
                <span>{user.role || 'user'}</span>
              </div>
              
              <div className="info-item">
                <label>Extension:</label>
                <span className="extension-badge">
                  {user.extensionId?.name || user.extensionName || 'N/A'}
                </span>
              </div>
              
              <div className="info-item">
                <label>Current Plan:</label>
                <span className="plan-badge">
                  {user.plan !== undefined ? `${user.plan} - ${['Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon', 'Zeta'][user.plan] || 'Unknown'}` : 'N/A'}
                </span>
              </div>
              
              <div className="info-item">
                <label>Created At:</label>
                <span>{user.date ? new Date(user.date).toLocaleString() : 'N/A'}</span>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="spinner"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUser; 